import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ClipboardList, ArrowRight, Calendar, Search, ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import {
    submitCollection,
    getMyCollections,
    getMyProcesses,
    getMyStatistics,
    updateCollection,
    getProcessDefaults,
    deleteCollection
} from '../actions/dailyCollectionActions';
import {
    SUBMIT_COLLECTION_RESET,
    GET_MY_COLLECTIONS_RESET,
    GET_MY_PROCESSES_RESET,
    GET_MY_STATISTICS_RESET,
    UPDATE_COLLECTION_RESET,
    DELETE_COLLECTION_RESET,
    GET_PROCESS_DEFAULTS_RESET
} from '../constants/dailyCollectionConstants';

// Enhanced Pagination Component
const EnhancedPagination = ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,
    className = "",
    showItemsPerPage = true,
    showInfo = true,
    maxVisiblePages = 5
}) => {
    const [itemsPerPageOptions] = useState([5, 10, 15, 20, 25, 50]);

    // Calculate visible page numbers
    const getVisiblePages = () => {
        const pages = [];
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    const visiblePages = getVisiblePages();
    const showStartEllipsis = visiblePages[0] > 2;
    const showEndEllipsis = visiblePages[visiblePages.length - 1] < totalPages - 1;

    // Calculate displayed items info
    const startItem = ((currentPage - 1) * itemsPerPage) + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const handlePrevious = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        onItemsPerPageChange(newItemsPerPage);
        // Adjust current page if necessary
        const newTotalPages = Math.ceil(totalItems / newItemsPerPage);
        if (currentPage > newTotalPages) {
            onPageChange(newTotalPages);
        }
    };

    if (totalPages <= 1 && !showInfo) return null;

    return (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-6 bg-white border-t border-gray-200 ${className}`}>
            {/* Items per page selector */}
            {showItemsPerPage && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Show</span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        {itemsPerPageOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <span>entries</span>
                </div>
            )}

            {/* Items info */}
            {showInfo && (
                <div className="text-sm text-gray-600 order-first sm:order-none">
                    {totalItems > 0 ? (
                        <>
                            Showing <span className="font-medium">{startItem}</span> to{' '}
                            <span className="font-medium">{endItem}</span> of{' '}
                            <span className="font-medium">{totalItems}</span> results
                        </>
                    ) : (
                        'No results found'
                    )}
                </div>
            )}

            {/* Pagination controls */}
            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    {/* Previous button */}
                    <button
                        onClick={handlePrevious}
                        disabled={currentPage === 1}
                        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${currentPage === 1
                            ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                            }`}
                    >
                        <ChevronLeft size={16} />
                        <span className="hidden sm:inline">Previous</span>
                    </button>

                    {/* First page */}
                    {visiblePages[0] > 1 && (
                        <>
                            <button
                                onClick={() => onPageChange(1)}
                                className="w-10 h-10 flex items-center justify-center text-sm font-medium rounded-md hover:bg-gray-50 hover:text-blue-600"
                            >
                                1
                            </button>
                            {showStartEllipsis && (
                                <div className="flex items-center justify-center w-10 h-10">
                                    <MoreHorizontal size={16} className="text-gray-400" />
                                </div>
                            )}
                        </>
                    )}

                    {/* Visible page numbers */}
                    {visiblePages.map(page => (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`w-10 h-10 flex items-center justify-center text-sm font-medium rounded-md transition-colors ${page === currentPage
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                                }`}
                        >
                            {page}
                        </button>
                    ))}

                    {/* Last page */}
                    {visiblePages[visiblePages.length - 1] < totalPages && (
                        <>
                            {showEndEllipsis && (
                                <div className="flex items-center justify-center w-10 h-10">
                                    <MoreHorizontal size={16} className="text-gray-400" />
                                </div>
                            )}
                            <button
                                onClick={() => onPageChange(totalPages)}
                                className="w-10 h-10 flex items-center justify-center text-sm font-medium rounded-md hover:bg-gray-50 hover:text-blue-600"
                            >
                                {totalPages}
                            </button>
                        </>
                    )}

                    {/* Next button */}
                    <button
                        onClick={handleNext}
                        disabled={currentPage === totalPages}
                        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${currentPage === totalPages
                            ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                            }`}
                    >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

const DailyCollection = () => {
    const dispatch = useDispatch();

    // Redux state selectors
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const submitCollectionInfo = useSelector((state) => state.submitCollectionInfo);
    const { loadingSubmitCollection, errorSubmitCollection, submitCollectionData } = submitCollectionInfo;

    const myCollectionsInfo = useSelector((state) => state.myCollectionsInfo);
    const { loadingMyCollections, errorMyCollections, myCollectionsData } = myCollectionsInfo;

    const myProcessesInfo = useSelector((state) => state.myProcessesInfo);
    const { loadingMyProcesses, errorMyProcesses, myProcessesData } = myProcessesInfo;

    const myStatisticsInfo = useSelector((state) => state.myStatisticsInfo);
    const { loadingMyStatistics, errorMyStatistics, myStatisticsData } = myStatisticsInfo;

    const updateCollectionInfo = useSelector((state) => state.updateCollectionInfo);
    const { loadingUpdateCollection, errorUpdateCollection, updateCollectionData } = updateCollectionInfo;

    const deleteCollectionInfo = useSelector((state) => state.deleteCollectionInfo);
    const { loadingDeleteCollection, errorDeleteCollection, deleteCollectionData } = deleteCollectionInfo;

    const processDefaultsInfo = useSelector((state) => state.processDefaultsInfo);
    const { loadingProcessDefaults, errorProcessDefaults, processDefaultsData } = processDefaultsInfo;


    // Local state
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({
        process: '',
        case: '',
        pos: '',
        res: '',
        rescase: '',
        totalCollection: '',
        norm: '',
        rbPercentage: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState({
        startDate: '',
        endDate: ''
    });


    const handleProcessChange = (e) => {
        const { name, value } = e.target;

        console.log('Process change:', name, value, 'isEditing:', isEditing);

        // Update form data
        setFormData({ ...formData, [name]: value });

        // Get defaults for this process only if not editing
        if (name === 'process' && value && !isEditing) {
            console.log('Dispatching getProcessDefaults for:', value);
            dispatch(getProcessDefaults(value));
        }
    };




    // Debug effect for process defaults
    useEffect(() => {
        console.log('Process defaults data changed:', processDefaultsData);
        console.log('Loading state:', loadingProcessDefaults);
        console.log('Error state:', errorProcessDefaults);

        if (processDefaultsData && !isEditing) {
            console.log('Setting form data with defaults:', processDefaultsData);
            setFormData(prev => ({
                ...prev,
                case: processDefaultsData.case || '',
                pos: processDefaultsData.pos || ''
            }));
        }
    }, [processDefaultsData, isEditing, loadingProcessDefaults, errorProcessDefaults]);
    // Get processes on component mount
    useEffect(() => {
        if (userInfo) {
            dispatch(getMyProcesses());
            dispatch(getMyStatistics());
        }
    }, [dispatch, userInfo]);

    // Set initial process when processes are loaded
    // Set initial process when processes are loaded
    useEffect(() => {
        if (myProcessesData && myProcessesData.processes && myProcessesData.processes.length > 0) {
            const firstProcess = myProcessesData.processes[0]._id;
            setFormData(prev => ({
                ...prev,
                process: firstProcess
            }));

            // Only fetch defaults if modal is open and not editing
            if (isOpen && !isEditing) {
                console.log('Getting defaults for initial process:', firstProcess);
                dispatch(getProcessDefaults(firstProcess));
            }
        }
    }, [myProcessesData, isOpen, isEditing, dispatch]);


    // Fetch collections when filters change
    useEffect(() => {
        if (userInfo) {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });

            if (searchTerm) params.append('search', searchTerm);
            if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
            if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);

            dispatch(getMyCollections(params.toString()));
        }
    }, [dispatch, userInfo, page, limit, searchTerm, dateFilter.startDate, dateFilter.endDate]);

    // Handle submit collection success
    // Handle submit collection success
    useEffect(() => {
        if (submitCollectionData) {
            const selectedProcess = formData.process;
            setFormData({
                process: selectedProcess,
                case: '',
                pos: '',
                res: '',
                rescase: '',
                totalCollection: '',
                norm: '',              // Fixed: use new fields
                rbPercentage: ''       // Fixed: use new fields
            });

            setIsEditing(false);
            setEditId(null);
            setIsOpen(false);

            // Refresh data
            dispatch(getMyStatistics());
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            dispatch(getMyCollections(params.toString()));

            // Reset after 3 seconds
            setTimeout(() => {
                dispatch({ type: SUBMIT_COLLECTION_RESET });
            }, 3000);
        }
    }, [submitCollectionData, dispatch, formData.process, page, limit]);

    // Handle update collection success
    useEffect(() => {
        if (updateCollectionData) {
            setIsEditing(false);
            setEditId(null);
            setIsOpen(false);

            // Refresh data
            dispatch(getMyStatistics());
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            dispatch(getMyCollections(params.toString()));

            // Reset after 3 seconds
            setTimeout(() => {
                dispatch({ type: UPDATE_COLLECTION_RESET });
            }, 3000);
        }
    }, [updateCollectionData, dispatch, page, limit]);

    // Handle delete collection success
    useEffect(() => {
        if (deleteCollectionData) {
            // Refresh data
            dispatch(getMyStatistics());
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            dispatch(getMyCollections(params.toString()));

            // Reset after 3 seconds
            setTimeout(() => {
                dispatch({ type: DELETE_COLLECTION_RESET });
            }, 3000);
        }
    }, [deleteCollectionData, dispatch, page, limit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const numericFields = ['case', 'pos', 'res', 'rescase', 'totalCollection', 'norm', 'rbPercentage'];

        if (name === 'process') {
            handleProcessChange(e);
        } else {
            const processedValue = numericFields.includes(name) ?
                (value === '' ? '' : Number(value)) : value;
            setFormData({ ...formData, [name]: processedValue });
        }
    };


    const handleSubmit = (e) => {
        e.preventDefault();

        if (isEditing) {
            dispatch(updateCollection(editId, formData));
        } else {
            dispatch(submitCollection(formData));
        }
    };

    const handleCloseModal = () => {
        setIsEditing(false);
        setEditId(null);

        const selectedProcess = myProcessesData?.processes?.length > 0 ? myProcessesData.processes[0]._id : '';
        setFormData({
            process: selectedProcess,
            case: '',
            pos: '',
            res: '',
            rescase: '',
            totalCollection: '',
            norm: '',
            rbPercentage: ''
        });

        setIsOpen(false);

        // Reset states
        dispatch({ type: SUBMIT_COLLECTION_RESET });
        dispatch({ type: UPDATE_COLLECTION_RESET });
        dispatch({ type: GET_PROCESS_DEFAULTS_RESET });
    };



    // Effect for when modal opens and process is selected
    useEffect(() => {
        if (isOpen && !isEditing && formData.process) {
            console.log('Modal opened, fetching defaults for:', formData.process);
            dispatch(getProcessDefaults(formData.process));
        }
    }, [isOpen, isEditing, formData.process, dispatch]);

    const handleUpdate = (collection) => {
        setFormData({
            process: collection.process._id,
            case: collection.case,
            pos: collection.pos,
            res: collection.res,
            rescase: collection.rescase,
            totalCollection: collection.totalCollection,
            norm: collection.norm || '',           // Fixed: use new fields
            rbPercentage: collection.rbPercentage || ''  // Fixed: use new fields
        });

        setIsEditing(true);
        setEditId(collection._id);
        setIsOpen(true);
    };
    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this collection?')) {
            dispatch(deleteCollection(id));
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleItemsPerPageChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const stats = myStatisticsData?.stats || {
        totalCases: 0,
        totalPos: 0,
        totalRes: 0,
        totalResCase: 0,
        totalCollection: 0,
        totalNorm: 0,            // Fixed: add new fields
        totalRbPercentage: 0     // Fixed: add new fields
    };

    const collections = myCollectionsData?.data || [];
    const totalPages = myCollectionsData?.totalPages || 1;
    const totalItems = myCollectionsData?.total || 0;
    const assignedProcesses = myProcessesData?.processes || [];

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-6xl mx-auto">
                {/* Welcome Card */}
                {userInfo && (
                    <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                        <div className="p-6 bg-blue-500 text-white">
                            <h1 className="text-2xl font-bold">Welcome, {userInfo.name}!</h1>
                            <p className="text-blue-100">Submit your daily collection data below</p>
                        </div>
                    </div>
                )}

                {/* Statistics Card */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-800">Your Overall Statistics</h2>
                    </div>

                    <div className="p-6">
                        {loadingMyStatistics ? (
                            <div className="text-center text-gray-500">Loading statistics...</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {stats && stats.totalCases > 0 && (
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <p className="text-sm text-blue-500">Total Cases</p>
                                        <p className="text-2xl font-bold text-blue-700">{stats.totalCases.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalPos > 0 && (
                                    <div className="bg-green-50 p-4 rounded-lg">
                                        <p className="text-sm text-green-500">Total POS</p>
                                        <p className="text-2xl font-bold text-green-700">{stats.totalPos.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalRes > 0 && (
                                    <div className="bg-purple-50 p-4 rounded-lg">
                                        <p className="text-sm text-purple-500">Total RES%</p>
                                        <p className="text-2xl font-bold text-purple-700">{stats.totalRes.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalResCase > 0 && (
                                    <div className="bg-yellow-50 p-4 rounded-lg">
                                        <p className="text-sm text-yellow-600">Total RES Case</p>
                                        <p className="text-2xl font-bold text-yellow-700">{stats.totalResCase.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalNorm > 0 && (
                                    <div className="bg-indigo-50 p-4 rounded-lg">
                                        <p className="text-sm text-indigo-500">Total NORM</p>
                                        <p className="text-2xl font-bold text-indigo-700">{stats.totalNorm.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalRbPercentage > 0 && (
                                    <div className="bg-pink-50 p-4 rounded-lg">
                                        <p className="text-sm text-pink-500">Total RB %</p>
                                        <p className="text-2xl font-bold text-pink-700">{stats.totalRbPercentage.toLocaleString()}%</p>
                                    </div>
                                )}

                                {stats && stats.totalCollection > 0 && (
                                    <div className="bg-red-50 p-4 rounded-lg">
                                        <p className="text-sm text-red-500">Total Collection</p>
                                        <p className="text-2xl font-bold text-red-700">
                                            ₹{stats.totalCollection.toLocaleString()}
                                        </p>
                                    </div>
                                )}


                                {(!stats ||
                                    (stats.totalCases <= 0 &&
                                        stats.totalPos <= 0 &&
                                        stats.totalRes <= 0 &&
                                        stats.totalResCase <= 0 &&
                                        stats.totalCollection <= 0 &&
                                        stats.totalNorm <= 0 &&           // Fixed: use new fields
                                        stats.totalRbPercentage <= 0)) && ( // Fixed: use new fields
                                        <div className="col-span-full text-center text-gray-500 py-4">
                                            No statistics available yet. Start submitting your collections!
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="p-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                    <input
                                        type="text"
                                        placeholder="Search by process or date..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <input
                                    type="date"
                                    value={dateFilter.startDate}
                                    onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <input
                                    type="date"
                                    value={dateFilter.endDate}
                                    onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setDateFilter({ startDate: '', endDate: '' });
                                        setPage(1);
                                    }}
                                    className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Collection Button */}
                <div className="flex justify-end items-center mb-4">
                    <button
                        onClick={() => {
                            setIsEditing(false);
                            setEditId(null);
                            const selectedProcess = assignedProcesses.length > 0 ? assignedProcesses[0]._id : '';
                            setFormData({
                                process: selectedProcess,
                                case: '',
                                pos: '',
                                res: '',
                                rescase: '',
                                totalCollection: '',
                                norm: '',
                                rbPercentage: ''
                            });

                            // Get defaults for selected process when opening modal
                            if (selectedProcess) {
                                dispatch(getProcessDefaults(selectedProcess));
                            }

                            setIsOpen(true);
                        }}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                        <ClipboardList size={18} />
                        Add Collection
                    </button>

                </div>

                {/* Success/Error Messages */}
                {(submitCollectionData || updateCollectionData || deleteCollectionData) && (
                    <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                        {submitCollectionData && 'Collection submitted successfully!'}
                        {updateCollectionData && 'Collection updated successfully!'}
                        {deleteCollectionData && 'Collection deleted successfully!'}
                    </div>
                )}

                {(errorSubmitCollection || errorUpdateCollection || errorDeleteCollection || errorMyCollections) && (
                    <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {errorSubmitCollection || errorUpdateCollection || errorDeleteCollection || errorMyCollections}
                    </div>
                )}

                {/* Modal for Add/Edit Collection */}
                {isOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-md w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                                    <ClipboardList className="mr-2" size={20} />
                                    {isEditing ? 'Update Collection' : 'Collection Form'}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-500 hover:text-gray-800 font-bold text-xl"
                                    aria-label="Close modal"
                                >
                                    &times;
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6">
                                <div className="mb-4">
                                    <label className="block text-gray-700 font-medium mb-2">Process</label>
                                    <select
                                        name="process"
                                        value={formData.process}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={isEditing}
                                    >
                                        <option value="">Select Process</option>
                                        {assignedProcesses.map((process) => (
                                            <option key={process._id} value={process._id}>
                                                {process.process}
                                            </option>
                                        ))}
                                    </select>
                                    {loadingProcessDefaults && (
                                        <p className="text-sm text-blue-500 mt-1">Loading defaults...</p>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">Case</label>
                                        <input
                                            type="number"
                                            name="case"
                                            value={formData.case}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />

                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">POS</label>
                                        <input
                                            type="number"
                                            name="pos"
                                            value={formData.pos}
                                            onChange={handleChange}
                                            min="0"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">RES%</label>
                                        <input
                                            type="number"
                                            name="res"
                                            value={formData.res}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">RES Case</label>
                                        <input
                                            type="number"
                                            name="rescase"
                                            value={formData.rescase}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">NORM</label>
                                        <input
                                            type="number"
                                            name="norm"
                                            value={formData.norm}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">RB %</label>
                                        <input
                                            type="number"
                                            name="rbPercentage"
                                            value={formData.rbPercentage}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-gray-700 font-medium mb-2">Total Collection</label>
                                    <input
                                        type="number"
                                        name="totalCollection"
                                        value={formData.totalCollection}
                                        onChange={handleChange}
                                        min="0"
                                        className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loadingSubmitCollection || loadingUpdateCollection}
                                    className={`w-full ${(loadingSubmitCollection || loadingUpdateCollection) ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"} text-white py-2 px-4 rounded-lg flex items-center justify-center transition-colors`}
                                >
                                    {(loadingSubmitCollection || loadingUpdateCollection) ? "Processing..." : isEditing ? "Update Collection" : "Submit Collection"}
                                    {!(loadingSubmitCollection || loadingUpdateCollection) && <ArrowRight className="ml-2" size={18} />}
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Collections Table */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-800 flex items-center">
                            <Calendar className="mr-2" size={20} />
                            {searchTerm ? 'Search Results' : 'Recent Submissions'}
                        </h2>
                    </div>

                    <div className="w-full overflow-x-auto">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden shadow-lg ring-1 ring-black ring-opacity-5">
                                {loadingMyCollections ? (
                                    <div className="p-6 text-center text-gray-500">Loading collections...</div>
                                ) : collections.length > 0 ? (
                                    <>
                                        <table className="min-w-[1100px] divide-y divide-gray-200 table-fixed">
                                            <thead className="bg-blue-50 text-xs text-blue-600 uppercase tracking-wider">
                                                <tr>
                                                    {[
                                                        { label: 'Date', width: '110px' },
                                                        { label: 'Process', width: '140px' },
                                                        { label: 'Case', width: '120px' },
                                                        { label: 'POS', width: '90px' },
                                                        { label: 'RES%', width: '70px' },
                                                        { label: 'RES Case', width: '100px' },
                                                        { label: 'NORM', width: '100px' },
                                                        { label: 'RB %', width: '100px' },
                                                        { label: 'Total', width: '130px' },
                                                        { label: 'Actions', width: '120px' },
                                                    ].map(({ label, width }) => (
                                                        <th key={label} className="px-3 py-3 text-left font-medium" style={{ width }}>
                                                            {label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-100">
                                                {collections.map((collection) => (
                                                    <tr key={collection._id} className="hover:bg-gray-50">
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '110px' }}>
                                                            {formatDate(collection.date)}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '140px' }}>
                                                            {collection.process?.process}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '120px' }}>
                                                            {collection.case || 0}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '90px' }}>
                                                            {collection.pos || 0}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '70px' }}>
                                                            {collection.res || 0}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '100px' }}>
                                                            {collection.rescase || 0}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm font-semibold text-indigo-600 truncate" style={{ width: '100px' }}>
                                                            {collection.norm || 0}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm font-semibold text-pink-600 truncate" style={{ width: '100px' }}>
                                                            {collection.rbPercentage || 0}%
                                                        </td>
                                                        <td className="px-3 py-4 text-sm font-semibold text-blue-600 truncate" style={{ width: '130px' }}>
                                                            ₹{(collection.totalCollection || 0).toLocaleString()}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm" style={{ width: '120px' }}>
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    onClick={() => handleUpdate(collection)}
                                                                    disabled={loadingUpdateCollection}
                                                                    className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-xs transition-colors disabled:opacity-50"
                                                                >
                                                                    ✏️ Update
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(collection._id)}
                                                                    disabled={loadingDeleteCollection}
                                                                    className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs transition-colors disabled:opacity-50"
                                                                >
                                                                    🗑️ Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Enhanced Pagination Component */}
                                        <EnhancedPagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            totalItems={totalItems}
                                            itemsPerPage={limit}
                                            onPageChange={handlePageChange}
                                            onItemsPerPageChange={handleItemsPerPageChange}
                                            maxVisiblePages={5}
                                            showItemsPerPage={true}
                                            showInfo={true}
                                        />
                                    </>
                                ) : (
                                    <div className="p-6 text-center text-gray-500">
                                        {searchTerm
                                            ? `No results found for "${searchTerm}". Try a different search term.`
                                            : 'No collection data found. Start submitting your daily collections!'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DailyCollection;